
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container, Paper, TextField, Button, Typography, Box, Alert,
  FormControl, InputLabel, Select, MenuItem, InputAdornment, IconButton, Divider
} from '@mui/material';
import { Person, Email, Lock, Visibility, VisibilityOff, Bolt, ArrowForward, Badge } from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const MotionPaper = motion(Paper);

const RegisterPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '', full_name: '', phone: '', company_name: '' });
  const [showSellerDialog, setShowSellerDialog] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.register(formData);
      login(response.data.access_token, response.data.user);
      toast.success('Account created!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '88vh', display: 'flex', alignItems: 'center', py: 4,
      position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(160deg, #fafbff 0%, #f0f9ff 40%, #eef2ff 100%)',
    }}>
      <Box sx={{ position: 'absolute', top: -100, left: -150, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(6,182,212,0.05) 0%, transparent 60%)', filter: 'blur(40px)' }} />
      <Box sx={{ position: 'absolute', bottom: -200, right: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.05) 0%, transparent 60%)', filter: 'blur(40px)' }} />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <MotionPaper
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          sx={{ p: { xs: 4, md: 6 }, borderRadius: 5, border: '1px solid rgba(148,163,184,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.04)' }}
          elevation={0}
        >
          <Box textAlign="center" sx={{ mb: 5 }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3,
              background: 'linear-gradient(135deg, #06b6d4, #4f46e5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(6,182,212,0.3)',
            }}>
              <Bolt sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.025em' }}>Create Account</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Join India's smartest B2B industrial marketplace
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Full Name" required sx={{ mb: 2.5 }}
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: '#a5b4fc', fontSize: 20 }} /></InputAdornment> }}
            />
            <TextField
              fullWidth label="Mobile Number" sx={{ mb: 2.5 }}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Badge sx={{ color: '#a5b4fc', fontSize: 20 }} /></InputAdornment> }}
            />
            <TextField
              fullWidth label="Company Name (optional)" sx={{ mb: 2.5 }}
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Badge sx={{ color: '#a5b4fc', fontSize: 20 }} /></InputAdornment> }}
            />
            <TextField
              fullWidth label="Email Address" type="email" required sx={{ mb: 2.5 }}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: '#a5b4fc', fontSize: 20 }} /></InputAdornment> }}
            />
            <TextField
              fullWidth label="Password" type={showPassword ? 'text' : 'password'} required sx={{ mb: 2.5 }}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              helperText="Minimum 8 characters"
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#a5b4fc', fontSize: 20 }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" aria-label="Toggle password">
                      {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth variant="contained" size="large" type="submit"
              disabled={loading} endIcon={!loading && <ArrowForward />}
              sx={{ mt: 3, py: 1.6 }}
            >
              {loading ? 'Creating account...' : 'Create Free Account'}
            </Button>
          </Box>

          <Divider sx={{ my: 3.5 }}><Typography variant="caption" color="text.secondary" fontWeight={500}>or</Typography></Divider>

          <Typography textAlign="center" variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Typography component={Link} to="/login" variant="body2"
              sx={{ color: 'primary.main', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
              Sign in
            </Typography>
          </Typography>
        </MotionPaper>
      </Container>

      {/* Seller opt-in removed: users can upload/list without opting in */}
    </Box>
  );
};

export default RegisterPage;


